import { formatDollars } from '@fruitful/fruitils';
import { InfoRow, RowList } from 'smoothie';

import {
    type ApexActivity,
    type ApexWithholding,
    formatApexDate,
} from '../../types/activities';
import { readableTransactionType } from '../../types/transactions';

const TransactionItem = (item: { description: string; value: string }) => {
    return <InfoRow title={item.description} value={item.value} />;
};

const formatWithholdingLabel = (w: ApexWithholding): string => {
    if (w.type === 'FEDERAL_IRA') {
        return 'Federal tax withheld';
    }
    if (w.type === 'STATE_IRA') {
        const stateLabel =
            w.state && w.state !== 'WITHHOLDING_STATE_UNSPECIFIED'
                ? ` (${w.state})`
                : '';
        return `State tax withheld${stateLabel}`;
    }
    return 'Tax withheld';
};

const formatWithholdingValue = (w: ApexWithholding): string => {
    const amount = formatDollars(Math.abs(parseFloat(w.amount.value)));
    const rate = w.rate != null ? parseFloat(w.rate.value) : 0;
    if (rate > 0) {
        return `${amount} (${(rate * 100).toFixed(0)}%)`;
    }
    return amount;
};

export const ApexAccountTransaction = ({
    transaction,
    apexAccount,
}: {
    transaction: ApexActivity;
    apexAccount: { accountNumber: string; nickname: string };
}) => {
    const showPrice = transaction.subtypeCategory === 'ALLOCATION';
    const showQuantity =
        transaction.subtypeCategory === 'ALLOCATION' ||
        (transaction.subtypeCategory === 'ACCOUNT_TRANSFER' &&
            transaction.assetSymbol != null);

    const showNetAmount = !(
        transaction.subtypeCategory === 'ACCOUNT_TRANSFER' &&
        transaction.assetSymbol != null
    );

    const grossValue = parseFloat(transaction.grossAmount.value);
    const netValue = parseFloat(transaction.netAmount.value);
    const hasWithholdings =
        transaction.withholdings?.length > 0 && grossValue !== netValue;

    return (
        <RowList marginTop={16}>
            <TransactionItem
                description="Activity date"
                value={formatApexDate(transaction.activityDate)}
            />
            <TransactionItem
                description="Settlement date"
                value={formatApexDate(transaction.settleDate)}
            />
            <TransactionItem
                description="Account number"
                value={apexAccount.accountNumber}
            />
            <TransactionItem
                description="Account name"
                value={apexAccount.nickname}
            />
            {transaction.assetSymbol != null && (
                <TransactionItem
                    description="Symbol"
                    value={transaction.assetSymbol}
                />
            )}
            <TransactionItem
                description="Description"
                value={transaction.activityDescription}
            />
            <TransactionItem
                description="Type"
                value={readableTransactionType(transaction)}
            />
            {showPrice && (
                <TransactionItem
                    description="Price"
                    value={formatDollars(parseFloat(transaction.price.value))}
                />
            )}
            {showQuantity && (
                <TransactionItem
                    description="Quantity"
                    value={transaction.quantity.value}
                />
            )}
            {showNetAmount && (
                <TransactionItem
                    description="Amount"
                    value={formatDollars(netValue)}
                />
            )}
            {hasWithholdings &&
                transaction.withholdings.map((w, i) => (
                    <TransactionItem
                        key={`withholding-${i}`}
                        description={formatWithholdingLabel(w)}
                        value={formatWithholdingValue(w)}
                    />
                ))}
            {hasWithholdings && (
                <TransactionItem
                    description="Amount after withholdings"
                    value={formatDollars(grossValue)}
                />
            )}
        </RowList>
    );
};
